export default function PrivacyPolicy() {
  return (
    <div className="bg-white text-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-10 lg:py-16">
        <h1 className="text-3xl font-bold text-slate-900">Happy Tail Tracker Privacy Policy</h1>
        <p className="mt-2 text-sm text-slate-600">
          <strong>Last updated:</strong> December 23, 2025
        </p>

        <p className="mt-6 leading-relaxed text-slate-800">
          Happy Tail Tracker (the &quot;App&quot;) is owned and operated by RocketMoss, Inc. and is designed to help pet
          parents organize and share health information about their animals on their own terms. This Privacy Policy
          explains what data we collect, how we use it, and the choices you have. By using the App, you confirm that you
          have read and understood this Privacy Policy. If you do not agree, please do not use the App or associated
          websites.
        </p>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">1. Information We Collect</h2>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>
              <strong>Account details</strong>: Information you provide when creating or managing an account (such as
              name, email address, and authentication credentials handled through Firebase Authentication).
            </li>
            <li>
              <strong>Pet profiles and health history</strong>: Pet names, species, breeds, birthdays, medication
              details, allergies, vaccinations, chronic conditions, health notes (including freeform text), and photo
              metadata or storage locations that you upload.
            </li>
            <li>
              <strong>Pet photos</strong>: Images you upload for your pets; we store these photos so they remain
              available in your account.
            </li>
            <li>
              <strong>Share tokens</strong>: Time-boxed tokens generated when you choose to share a pet summary via the
              web view. Tokens map the shared link back to the relevant pet record until you revoke or the token
              expires.
            </li>
            <li>
              <strong>Usage data</strong>: Device information, app activity, diagnostics, and other technical data
              collected through standard logging to keep the App stable and secure.
            </li>
            <li>
              <strong>Support communications</strong>: Messages, attachments, or other information you send when you
              contact us for help.
            </li>
          </ul>
          <p className="text-slate-800">
            We do not knowingly collect information directly from veterinarians, clinics, or other third parties about
            you or your pets. Any information they view comes solely from what you choose to share.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">2. How We Use Information</h2>
          <p className="text-slate-800">We use your information to:</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>Provide and maintain the App, including synchronizing data across mobile and web experiences.</li>
            <li>Securely store your pet records and related notes for your personal use.</li>
            <li>
              Make your pet photos available solely to you and those you expressly authorize. We do <strong>not</strong>{' '}
              otherwise process, monetize, or sell these photos to any third party for any purpose.
            </li>
            <li>Generate, validate, and honor time-limited share tokens when you decide to share information.</li>
            <li>
              Troubleshoot issues, perform analytics, and improve features (for example, understanding which screens
              crash so we can fix them).
            </li>
            <li>Communicate with you about updates, security alerts, and changes to the App.</li>
            <li>Comply with legal obligations and enforce our Terms of Service.</li>
          </ul>
          <p className="text-slate-800">
            We do <strong>not</strong> sell your personal information. We do <strong>not</strong> share your pet data
            with clinics, vendors, or advertisers unless you expressly direct us to do so.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">3. Legal Basis for Processing</h2>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>
              <strong>Performance of a contract</strong>: To deliver the services you request by using the App.
            </li>
            <li>
              <strong>Legitimate interests</strong>: To secure and improve the App in ways that do not override your
              rights.
            </li>
            <li>
              <strong>Consent</strong>: When you generate share tokens or provide optional information, you are
              consenting to that specific use. You can withdraw consent at any time by revoking the token or deleting the
              data.
            </li>
            <li>
              <strong>Legal obligations</strong>: To comply with applicable laws, regulations, or court orders.
            </li>
          </ul>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">4. Data Ownership and Control</h2>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>You own every piece of pet and personal data you upload.</li>
            <li>You can view, edit, or delete your information within the App at any time.</li>
            <li>
              You can revoke a share token at any moment by deleting it from the mobile App or the underlying Firebase
              record; this immediately blocks access to the shared web summary.
            </li>
            <li>
              If you close your account or ask us to delete information, we will remove or anonymize the associated data
              subject to any legal obligations to retain it.
            </li>
          </ul>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">5. Data Sharing and Disclosure</h2>
          <p className="text-slate-800">We share information only in the limited circumstances below:</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>
              <strong>Service providers</strong>: We rely on Firebase and other infrastructure providers to host data,
              authenticate users, and deliver push notifications or analytics. These providers are contractually
              obligated to safeguard your data and may only process it on our behalf.
            </li>
            <li>
              <strong>Authorized viewers</strong>: Anyone with a valid share token link can view the associated pet
              summary until you revoke the token or it expires. Tokens are intentionally short-lived and can be revoked
              at any time from the mobile App.
            </li>
            <li>
              <strong>Legal requirements</strong>: We may disclose information if required by law or if we believe in
              good faith that disclosure is necessary to protect our rights, investigate fraud, or respond to a lawful
              request.
            </li>
          </ul>
          <p className="text-slate-800">
            We do <strong>not</strong> provide clinics or third parties with ongoing access to your data. They can only
            see what you explicitly share through a tokenized link, and only for as long as you allow.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">6. Data Retention</h2>
          <p className="text-slate-800">
            We retain your information for as long as your account is active or as needed to provide the App. When you
            delete data, it is removed from active Firebase records and scheduled for deletion from backups in
            accordance with our retention policies. We may retain minimal information for legal compliance, dispute
            resolution, or enforcement of agreements. When you remove a photo, we delete it from our servers (subject to
            backup timelines), but this does not remove copies stored on your devices or backups you control.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">7. Security</h2>
          <p className="text-slate-800">
            We implement technical and organizational measures&mdash;including encrypted connections, access controls,
            and monitoring&mdash;to protect your information. No system is ever completely secure, so we encourage you
            to keep devices updated, use strong passwords, and contact us immediately if you suspect unauthorized
            access.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">8. Your Rights</h2>
          <p className="text-slate-800">Depending on where you live, you may have rights to:</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>Access the information we store about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your data.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Receive a copy of your data in a portable format.</li>
          </ul>
          <p className="text-slate-800">
            To exercise these rights, email us at{' '}
            <a href="mailto:support@happytailtracker.com" className="text-blue-600 underline">support@happytailtracker.com</a>.
            We may need to verify your identity before fulfilling a request.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">9. Children&apos;s Privacy</h2>
          <p className="text-slate-800">
            The App is intended for use by adults. If we learn that we have collected personal information from a child
            without verifiable parental consent, we will delete that information as quickly as possible. If you believe
            a child has provided us with personal information, please contact us.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">10. International Users</h2>
          <p className="text-slate-800">
            We store data in regions supported by our infrastructure providers (currently the United States). By using
            the App, you understand that your information may be transferred to, processed, and stored outside of your
            home country, where privacy laws may be different. We will take reasonable steps to protect your data in
            line with this Privacy Policy.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">11. Changes to This Policy</h2>
          <p className="text-slate-800">
            We may update this Privacy Policy from time to time. If we make material changes, we will notify you through
            the App, by email, or by posting an update here. Continued use of the App after a change becomes effective
            constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-semibold text-slate-900">12. Contact Us</h2>
          <p className="text-slate-800">If you have questions or concerns about privacy, or if you want to exercise your rights, please contact us:</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-800">
            <li>
              Email: <a href="mailto:support@happytailtracker.com" className="text-blue-600 underline">support@happytailtracker.com</a>
            </li>
          </ul>
          <p className="text-slate-800">
            We are committed to putting pets and pet parents first. Thank you for trusting Happy Tail Tracker with your
            pet&apos;s health story.
          </p>
        </section>
      </div>
    </div>
  );
}
